import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import { groupCapabilitiesByFriendlyLabel } from "./RoleCard";
import { accessBadge, accessSummary } from "./capabilityAccess";

// Row-badge styling. Deliberately a local span rather than the shared Badge:
// Badge's `sm` size is a 12px pill sized for status columns, and its amber
// pairs the darkest amber token with the palest amber background at
// font-medium — legible, but thin for text this small. Same tokens, heavier
// weight, and a hairline edge in the badge's own hue, which is where the
// extra contrast comes from (the palette has no darker amber to reach for).
const BADGE_TONE = {
  info: { cls: "bg-primary-soft text-primary-hover", border: "hsl(var(--primary) / 0.28)" },
  warning: { cls: "bg-warning-soft text-warning", border: "hsl(var(--warning) / 0.38)" },
  danger: { cls: "bg-danger-soft text-danger", border: "hsl(var(--danger) / 0.30)" },
};

// Slate-500. A grey glyph would read as disabled and the app's primary petrol
// would read as interactive; a blue-grey sits between, muted but present.
// Hardcoded like RightSheet's slate backdrop — the token palette has no
// neutral in this family.
const INCLUDED_ICON_COLOR = "#64748B";

// The read-only capability list for a role, bucketed by the same friendly
// group labels RoleCard's footprint summary uses so the two always agree on
// how a raw group key reads. Presentational only — it knows nothing about
// sheets or modals, so both the template picker's detail level and any future
// read-only surface can render an identical list.
//
// Rows, not chips. An earlier version rendered each capability as a bordered
// pill, which reads as a control — a filter you can toggle or a token you can
// remove — on a surface where nothing is editable.
//
// The leading glyph is a check *in a circle*, not a bare tick: a standalone
// tick is the same mark a checked checkbox carries, so on a read-only list it
// invites a click that does nothing. A circled check reads as a status mark —
// "included" — rather than a selection. A disabled checkbox would be worse
// still: it implies the list is editable somewhere, by someone.
//
// Each section states its elevation once, in the descriptor line, instead of
// badging every row. A configuration-heavy area otherwise rendered six
// identical pills that said what the section heading already said; the rows
// that keep a badge are now the exceptions worth reading. See capabilityAccess.
export default function RoleCapabilityGroups({
  capabilities = [],
  capByCode,
  emptyMessage = "This template carries no capabilities.",
}) {
  const groups = useMemo(
    () => groupCapabilitiesByFriendlyLabel(capabilities, capByCode).sort(([a], [b]) => a.localeCompare(b)),
    [capabilities, capByCode]
  );

  if (groups.length === 0) {
    return <p className="text-sm text-text-2">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-8">
      {groups.map(([label, caps]) => {
        const descriptor = accessSummary(caps.map((c) => c.code));
        return (
          <section key={label}>
            {/* Count sits beside the title as plain muted text, not a badge:
                it is a fact about the section, not a status worth a pill. */}
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-semibold text-text m-0" data-testid="capability-group-label">
                {label}
              </h3>
              <span className="text-xs text-text-3" data-testid="capability-group-count">
                {caps.length}
              </span>
            </div>
            {descriptor && (
              <p className="mt-2 text-xs text-text-3" data-testid="capability-group-description">
                {descriptor}
              </p>
            )}

            <ul className="mt-3 list-none p-0 m-0">
              {caps.map((c, i) => {
                const badge = accessBadge(c.code);
                const tone = badge ? BADGE_TONE[badge.tone] : null;
                return (
                  <li
                    key={c.code}
                    data-testid="capability-row"
                    // min-height rather than a height utility: this project's
                    // Tailwind config overrides theme.height with a tiny custom
                    // scale, so most h-* classes are no-ops here.
                    style={{ minHeight: "48px" }}
                    className="flex items-center gap-3"
                  >
                    <FontAwesomeIcon
                      icon={icon({ name: "circle-check", style: "regular" })}
                      className="shrink-0"
                      style={{ fontSize: 16, color: INCLUDED_ICON_COLOR }}
                    />
                    {/* The separator lives on this inner element, which starts
                        after the glyph, so the rule is inset to the text column
                        rather than spanning the full row. self-stretch makes it
                        full row height so the border lands at the bottom edge.
                        The last row of a section carries none — the 32px gap to
                        the next heading is the separation. */}
                    <div
                      className={`flex flex-1 items-center gap-3 min-w-0 self-stretch ${
                        i < caps.length - 1 ? "border-b border-solid border-border" : ""
                      }`}
                    >
                      <span className="text-sm text-text">{c.name}</span>
                      {badge && (
                        <span
                          className={`ml-auto shrink-0 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none ${tone.cls}`}
                          style={{ borderWidth: 1, borderStyle: "solid", borderColor: tone.border }}
                        >
                          {badge.label}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

RoleCapabilityGroups.propTypes = {
  capabilities: PropTypes.arrayOf(PropTypes.string),
  capByCode: PropTypes.instanceOf(Map).isRequired,
  emptyMessage: PropTypes.string,
};
